"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, PenTool, CircleCheck as CheckCircle2, Stethoscope, SearchCheck, FileCheck2, RefreshCw, ShieldCheck, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const steps = [{
  step: "01",
  title: "Peer-reviewed research",
  summary: "Every topic begins with primary literature from accredited veterinary journals, clinical trials, and academic pathology sources.",
  detail: "AVMA, PubMed & veterinary university repositories"
}, {
  step: "02",
  title: "Specialist medical writing",
  summary: "Experienced pet health journalists draft structured articles, translating intricate clinical data into clear owner guidance.",
  detail: "Led by credentialed veterinary journalists"
}, {
  step: "03",
  title: "Editorial clarity review",
  summary: "In-house senior editors evaluate tone, reading rhythm, and plain-language accessibility while removing jargon and ambiguous claims.",
  detail: "Plain-language accessibility standard"
}, {
  step: "04",
  title: "Board veterinary inspection",
  summary: "Licensed practicing veterinarians cross-examine dosages, symptom matrices, clinical risks, and diagnostic pathways for accuracy.",
  detail: "Signed off by licensed DVM practitioners"
}, {
  step: "05",
  title: "Citation & source audit",
  summary: "Every statistic, medication mention, and dietary reference is indexed directly to verifiable academic publications.",
  detail: "Direct DOI links & verified citations"
}, {
  step: "06",
  title: "Publication & takeaway design",
  summary: "Content is styled with quick-reference advisory summaries, diagnostic callouts, and clear warning signs for urgent care.",
  detail: "Clinical takeaways & symptom highlights"
}, {
  step: "07",
  title: "Annual medical re-auditing",
  summary: "Articles are scheduled for rolling reviews every 12 months to incorporate emerging pharmaceutical discoveries and revised guidelines.",
  detail: "12-month recurring review cycle"
}];
const highlights = [{
  title: "Independent scrutiny",
  text: "We do not accept paid editorial inclusions or sponsored medical conclusions."
}, {
  title: "Evidence over opinion",
  text: "Clinical suggestions require published veterinary literature or veterinary board consensus."
}, {
  title: "Full transparency",
  text: "Author credentials, reviewing veterinarian names, and source bibliographies are visible on every guide."
}];
export default function EditorialProcess() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  const stepIcons = [BookOpen, PenTool, CheckCircle2, Stethoscope, SearchCheck, FileCheck2, RefreshCw];
  return <section id="editorial-process" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 md:mb-18">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            <ShieldCheck className="size-3.5 text-accent" />
            <span>Rigorous publication pipeline</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground text-balance">
            How we research, verify, and maintain pet care guidance
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every health article follows a seven-stage clinical review workflow
            to ensure pet owners receive factual, safe, and current information.
          </p>
        </div>

        {/* 7-Step Workflow Layout */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4 lg:gap-3 items-stretch mb-16">
          {steps.map((item, i) => {
          const IconComponent = stepIcons[i % stepIcons.length];
          return <motion.div key={item.step} data-index={i} variants={itemVariants} className="group relative flex flex-col justify-between bg-card text-card-foreground border border-border rounded-xl p-5 transition-[border-color,box-shadow] duration-200 hover:border-border hover:shadow-md">
                {/* Step Marker & Top Icon */}
                <div>
                  <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b border-border/80">
                    <span className="font-mono text-xs font-bold tracking-widest text-muted-foreground/90">
                      {item.step}
                    </span>
                    <div className="size-8 rounded-md bg-muted flex items-center justify-center text-foreground group-hover:text-accent transition-colors">
                      <IconComponent className="size-4" />
                    </div>
                  </div>

                  <h3 className="font-serif text-lg font-semibold text-card-foreground leading-snug mb-2">
                    {item.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-4">
                    {item.summary}
                  </p>
                </div>

                {/* Sub-label footer */}
                <div className="pt-3 border-t border-border/60 text-[11px] font-medium text-muted-foreground">
                  <span className="block text-foreground/80 font-semibold mb-0.5">
                    Standard:
                  </span>
                  {item.detail}
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* Clinical Governance Callout / Assurance Box */}
        <div className="rounded-xl border border-border bg-card p-6 sm:p-8 lg:p-10 relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-accent" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-4">
              <span className="text-xs font-bold uppercase tracking-wider text-accent block mb-1">
                Clinical governance
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                Our promise to pet parents
              </h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Health advice affects lives. We hold our content to the same
                methodological standards used in human clinical publications.
              </p>

              <div className="mt-6">
                <Button asChild size="default" className="w-full sm:w-auto">
                  <Link to="/PetHealthPage">
                    Explore veterinary guides
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 lg:pt-0 lg:border-l lg:border-border lg:pl-8">
              {highlights.map((highlight, index) => <div key={highlight.title} data-index={index} className="flex flex-col">
                  <h4 className="font-sans text-sm font-semibold text-foreground mb-1.5">
                    {highlight.title}
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {highlight.text}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}